import type { OrganizationTeam, UserResource } from "@/engine/types";
import { EMPLOYMENT_LABEL, GRADE_LABEL, TEAM_COLORS } from "./resource-meta";
import {
  ORG_EXCEL_SCHEMA,
  ORG_SHEET,
  WORKING_DAYS_PER_MONTH,
  normKey,
  parseActive,
  parseColor,
  parseEmployment,
  parseGrade,
  parseMoney,
  parseSkills,
  type OrgExcelCatalog,
  type ParsedPersonRow,
  type ParsedTeamRow,
} from "./org-excel-schema";

export type OrgIssueSeverity = "error" | "warning" | "info";

export interface OrgIssue {
  severity: OrgIssueSeverity;
  sheet: string;
  excelRow: number;
  field: string;
  value: string;
  message: string;
  action: string;
}

export type OrgRowAction = "create" | "update" | "unchanged" | "skip";

export interface OrgTeamPreviewRow {
  excelRow: number;
  action: OrgRowAction;
  tempKey: string;
  id?: string;
  name: string;
  description?: string;
  color?: string;
  leaderRaw: string;
  leaderResourceId?: string;
  leaderTempKey?: string;
  changedFields: string[];
  issues: OrgIssue[];
}

export interface OrgPersonPreviewRow {
  excelRow: number;
  action: OrgRowAction;
  tempKey: string;
  id?: string;
  displayName: string;
  email: string;
  roleTitle: string;
  teamRaw: string;
  teamId?: string;
  teamTempKey?: string;
  employmentType?: UserResource["employmentType"];
  grade?: UserResource["grade"];
  defaultMonthlyRate?: number;
  defaultDailyRate?: number;
  skills: string[];
  notes?: string;
  isActive: boolean;
  changedFields: string[];
  issues: OrgIssue[];
}

export interface OrgImportPreview {
  schemaVersion: string | null;
  fileName: string;
  blocking: boolean;
  issues: OrgIssue[];
  teams: OrgTeamPreviewRow[];
  people: OrgPersonPreviewRow[];
  applyableCount: number;
  errorCount: number;
  warningCount: number;
  createCount: number;
  updateCount: number;
}

export interface OrgExcelParseInput {
  fileName: string;
  schemaVersion: string | null;
  hasGuide: boolean;
  hasTeams: boolean;
  hasPeople: boolean;
  assignmentRowCount: number;
  teams: ParsedTeamRow[];
  people: ParsedPersonRow[];
  extraIssues?: OrgIssue[];
}

function issue(partial: Omit<OrgIssue, "action"> & { action?: string }): OrgIssue {
  return {
    action: partial.severity === "error" ? "이 행 건너뜀" : partial.severity === "warning" ? "적용하되 이 값은 무시" : "자동 처리",
    ...partial,
  };
}

function sameText(a?: string, b?: string): boolean {
  return (a ?? "") === (b ?? "");
}

function sameList(a: string[], b: string[]): boolean {
  return a.join("\u0001") === b.join("\u0001");
}

function resolveLeader(
  raw: string,
  people: OrgPersonPreviewRow[],
  catalog: OrgExcelCatalog,
): { resourceId?: string; tempKey?: string; ambiguous?: boolean } {
  const key = normKey(raw);
  if (!key) return {};
  const peopleHits = people.filter((p) => p.action !== "skip" && (normKey(p.email) === key || normKey(p.displayName) === key));
  if (peopleHits.length === 1) {
    const hit = peopleHits[0]!;
    return hit.id ? { resourceId: hit.id } : { tempKey: hit.tempKey };
  }
  if (peopleHits.length > 1) return { ambiguous: true };
  const catalogHits = Object.values(catalog.resources).filter((r) => normKey(r.email) === key || normKey(r.displayName) === key);
  if (catalogHits.length === 1) return { resourceId: catalogHits[0]!.id };
  if (catalogHits.length > 1) return { ambiguous: true };
  return {};
}

export function previewOrgImport(input: OrgExcelParseInput, catalog: OrgExcelCatalog): OrgImportPreview {
  const fileIssues: OrgIssue[] = [...(input.extraIssues ?? [])];

  if (!input.hasGuide) {
    fileIssues.push(issue({ severity: "error", sheet: ORG_SHEET.guide, excelRow: 0, field: "안내", value: "", message: "안내 탭이 없습니다" }));
  }
  if (input.schemaVersion !== ORG_EXCEL_SCHEMA) {
    fileIssues.push(
      issue({
        severity: "error",
        sheet: ORG_SHEET.guide,
        excelRow: 0,
        field: "스키마버전",
        value: input.schemaVersion ?? "",
        message: input.schemaVersion ? `스키마 ${input.schemaVersion}은 지원하지 않습니다. ${ORG_EXCEL_SCHEMA} 파일이 필요합니다` : `안내 탭에 스키마버전 ${ORG_EXCEL_SCHEMA}이 없습니다`,
      }),
    );
  }
  if (!input.hasTeams) {
    fileIssues.push(issue({ severity: "error", sheet: ORG_SHEET.teams, excelRow: 0, field: "팀", value: "", message: "팀 탭이 없습니다" }));
  }
  if (!input.hasPeople) {
    fileIssues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: 0, field: "인력", value: "", message: "인력 탭이 없습니다" }));
  }
  if (input.assignmentRowCount > 0) {
    fileIssues.push(
      issue({
        severity: "warning",
        sheet: ORG_SHEET.assignments,
        excelRow: 0,
        field: "참고_투입",
        value: `${input.assignmentRowCount}행`,
        message: "참고_투입은 읽기 전용입니다. 이 화면에서는 적용하지 않습니다",
        action: "시트 전체 무시",
      }),
    );
  }

  const blocking = fileIssues.some((i) => i.severity === "error");
  const teamRows: OrgTeamPreviewRow[] = [];
  const personRows: OrgPersonPreviewRow[] = [];

  if (!blocking) {
    const seenTeamIds = new Map<string, number>();
    const seenTeamNames = new Map<string, number>();

    for (const row of input.teams) {
      const issues: OrgIssue[] = [];
      const name = row.name.trim();
      const id = row.id.trim();
      const tempKey = `team:${row.excelRow}`;

      if (!name) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.teams, excelRow: row.excelRow, field: "팀이름", value: "", message: "팀 이름은 필수입니다" }));
      }

      if (id) {
        const prev = seenTeamIds.get(id);
        if (prev) {
          issues.push(issue({ severity: "error", sheet: ORG_SHEET.teams, excelRow: row.excelRow, field: "id", value: id, message: `${prev}행과 같은 id입니다` }));
        } else {
          seenTeamIds.set(id, row.excelRow);
        }
      }
      if (name) {
        const nameKey = normKey(name);
        const prev = seenTeamNames.get(nameKey);
        if (prev) {
          issues.push(issue({ severity: "error", sheet: ORG_SHEET.teams, excelRow: row.excelRow, field: "팀이름", value: name, message: `${prev}행과 같은 팀 이름입니다` }));
        } else {
          seenTeamNames.set(nameKey, row.excelRow);
        }
      }

      let existing: OrganizationTeam | undefined;
      if (id) {
        existing = catalog.teams[id];
        if (!existing) {
          issues.push(issue({ severity: "error", sheet: ORG_SHEET.teams, excelRow: row.excelRow, field: "id", value: id, message: "이 워크스페이스에 없는 팀 id입니다" }));
        }
      } else if (name) {
        existing = Object.values(catalog.teams).find((t) => normKey(t.name) === normKey(name));
      }

      const color = parseColor(row.color);
      if (row.color.trim() && !color) {
        issues.push(issue({ severity: "warning", sheet: ORG_SHEET.teams, excelRow: row.excelRow, field: "컬러", value: row.color, message: "알 수 없는 색입니다. 기본 색을 씁니다" }));
      }

      const hasError = issues.some((i) => i.severity === "error");
      const nextColor = color ?? existing?.color ?? TEAM_COLORS[0]!.value;
      const nextDescription = row.description.trim() || undefined;
      const changedFields: string[] = [];
      let action: OrgRowAction = "skip";

      if (!hasError && existing) {
        if (!sameText(existing.name, name)) changedFields.push("팀이름");
        if (!sameText(existing.description, nextDescription)) changedFields.push("설명");
        if (!sameText(existing.color, nextColor)) changedFields.push("컬러");
        if (row.leader.trim() && normKey(row.leader) !== normKey(catalog.resources[existing.leaderId ?? ""]?.displayName ?? catalog.resources[existing.leaderId ?? ""]?.email ?? "")) {
          changedFields.push("리더");
        }
        if (!row.leader.trim() && existing.leaderId) changedFields.push("리더");
        action = changedFields.length ? "update" : "unchanged";
      } else if (!hasError) {
        action = "create";
        changedFields.push("신규");
      }

      teamRows.push({
        excelRow: row.excelRow,
        action,
        tempKey,
        id: existing?.id,
        name,
        description: nextDescription,
        color: nextColor,
        leaderRaw: row.leader.trim(),
        changedFields: action === "skip" ? [] : changedFields,
        issues,
      });
    }

    const seenPersonIds = new Map<string, number>();
    for (const row of input.people) {
      const issues: OrgIssue[] = [];
      const displayName = row.name.trim();
      const id = row.id.trim();
      const tempKey = `person:${row.excelRow}`;
      const email = row.email.trim();
      const roleTitle = row.roleTitle.trim();
      const teamRaw = row.team.trim();

      if (!displayName) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "이름", value: "", message: "이름은 필수입니다" }));
      }
      if (!roleTitle) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "직책", value: "", message: "직책은 필수입니다" }));
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "이메일", value: email, message: "이메일 형식이 아닙니다" }));
      }

      if (id) {
        const prev = seenPersonIds.get(id);
        if (prev) {
          issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "id", value: id, message: `${prev}행과 같은 id입니다` }));
        } else {
          seenPersonIds.set(id, row.excelRow);
        }
      }

      let existing: UserResource | undefined;
      if (id) {
        existing = catalog.resources[id];
        if (!existing) {
          issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "id", value: id, message: "이 워크스페이스에 없는 인력 id입니다" }));
        }
      }

      const employmentType = parseEmployment(row.employment);
      if (!row.employment.trim()) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "고용형태", value: "", message: "고용형태는 필수입니다" }));
      } else if (!employmentType) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "고용형태", value: row.employment, message: `허용 값: ${Object.values(EMPLOYMENT_LABEL).join(", ")}` }));
      }

      const grade = parseGrade(row.grade);
      if (!row.grade.trim()) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "등급", value: "", message: "등급은 필수입니다" }));
      } else if (!grade) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "등급", value: row.grade, message: `허용 값: ${Object.values(GRADE_LABEL).join(", ")}` }));
      }

      let defaultMonthlyRate: number | undefined;
      if (row.monthlyRate.trim()) {
        const money = parseMoney(row.monthlyRate);
        if (money === null || money < 0) {
          issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "월단가", value: row.monthlyRate, message: "0 이상의 숫자여야 합니다" }));
        } else {
          defaultMonthlyRate = money;
        }
      }

      const isActive = parseActive(row.active);
      if (isActive === null) {
        issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "활성", value: row.active, message: "Y 또는 N으로 입력하세요" }));
      }

      if (row.utilization.trim()) {
        issues.push(
          issue({
            severity: "warning",
            sheet: ORG_SHEET.people,
            excelRow: row.excelRow,
            field: "가동률",
            value: row.utilization,
            message: "가동률은 읽기 전용입니다. 플랜 투입에서 다시 계산합니다",
          }),
        );
      }

      let teamId: string | undefined;
      let teamTempKey: string | undefined;
      if (teamRaw) {
        const fileTeam = teamRows.find((t) => t.action !== "skip" && (t.id === teamRaw || normKey(t.name) === normKey(teamRaw)));
        const catalogTeam = catalog.teams[teamRaw] ?? Object.values(catalog.teams).find((t) => normKey(t.name) === normKey(teamRaw));
        if (fileTeam) {
          if (fileTeam.id) teamId = fileTeam.id;
          else teamTempKey = fileTeam.tempKey;
        } else if (catalogTeam) {
          teamId = catalogTeam.id;
        } else {
          issues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: row.excelRow, field: "팀", value: teamRaw, message: "팀 탭과 워크스페이스에 없는 팀입니다. 팀 탭에 먼저 추가하세요" }));
        }
      }

      const skills = parseSkills(row.skills);
      const notes = row.notes.trim() || undefined;
      const hasError = issues.some((i) => i.severity === "error");
      const changedFields: string[] = [];
      let action: OrgRowAction = "skip";
      const nextActive = isActive ?? true;
      const defaultDailyRate = defaultMonthlyRate !== undefined ? Math.round(defaultMonthlyRate / WORKING_DAYS_PER_MONTH) : existing?.defaultDailyRate;

      if (!hasError && existing) {
        if (!sameText(existing.displayName, displayName)) changedFields.push("이름");
        if (!sameText(existing.email, email)) changedFields.push("이메일");
        if (!sameText(existing.roleTitle, roleTitle)) changedFields.push("직책");
        if ((existing.teamId ?? "") !== (teamId ?? existing.teamId ?? "") && !teamTempKey) {
          if ((existing.teamId ?? "") !== (teamId ?? "")) changedFields.push("팀");
        } else if (teamTempKey || (existing.teamId ?? "") !== (teamId ?? "")) {
          changedFields.push("팀");
        }
        if (employmentType && existing.employmentType !== employmentType) changedFields.push("고용형태");
        if (grade && existing.grade !== grade) changedFields.push("등급");
        if ((existing.defaultMonthlyRate ?? undefined) !== defaultMonthlyRate) changedFields.push("월단가");
        if (!sameList(existing.skills, skills)) changedFields.push("스킬");
        if (!sameText(existing.notes, notes)) changedFields.push("메모");
        if ((existing.isActive !== false) !== nextActive) changedFields.push("활성");
        action = changedFields.length ? "update" : "unchanged";
      } else if (!hasError) {
        action = "create";
        changedFields.push("신규");
      }

      personRows.push({
        excelRow: row.excelRow,
        action,
        tempKey,
        id: existing?.id,
        displayName,
        email,
        roleTitle,
        teamRaw,
        teamId,
        teamTempKey,
        employmentType: employmentType ?? undefined,
        grade: grade ?? undefined,
        defaultMonthlyRate,
        defaultDailyRate,
        skills,
        notes,
        isActive: nextActive,
        changedFields: action === "skip" ? [] : changedFields,
        issues,
      });
    }

    for (const team of teamRows) {
      if (team.action === "skip" || !team.leaderRaw) continue;
      const resolved = resolveLeader(team.leaderRaw, personRows, catalog);
      if (resolved.ambiguous) {
        team.issues.push(issue({ severity: "warning", sheet: ORG_SHEET.teams, excelRow: team.excelRow, field: "리더", value: team.leaderRaw, message: "같은 이름/이메일의 인력이 여러 명입니다. 리더는 비워 둡니다" }));
        if (!team.changedFields.includes("리더")) team.changedFields.push("리더");
      } else if (resolved.resourceId || resolved.tempKey) {
        team.leaderResourceId = resolved.resourceId;
        team.leaderTempKey = resolved.tempKey;
      } else {
        team.issues.push(issue({ severity: "warning", sheet: ORG_SHEET.teams, excelRow: team.excelRow, field: "리더", value: team.leaderRaw, message: "인력 탭과 워크스페이스에서 리더를 찾지 못했습니다" }));
      }
    }
  }

  const rowIssues = [...teamRows, ...personRows].flatMap((r) => r.issues);
  const allIssues = [...fileIssues, ...rowIssues];
  const applyable = [...teamRows, ...personRows].filter((r) => r.action === "create" || r.action === "update");
  const errorCount = allIssues.filter((i) => i.severity === "error").length;
  const warningCount = allIssues.filter((i) => i.severity === "warning").length;

  return {
    schemaVersion: input.schemaVersion,
    fileName: input.fileName,
    blocking,
    issues: allIssues,
    teams: teamRows,
    people: personRows,
    applyableCount: applyable.length,
    errorCount,
    warningCount,
    createCount: applyable.filter((r) => r.action === "create").length,
    updateCount: applyable.filter((r) => r.action === "update").length,
  };
}

export function applyOrgImportPreview(
  preview: OrgImportPreview,
  current: OrgExcelCatalog,
  newId: (prefix: string) => string,
): OrgExcelCatalog {
  if (preview.blocking) return { teams: { ...current.teams }, resources: { ...current.resources } };

  const teams: Record<string, OrganizationTeam> = { ...current.teams };
  const resources: Record<string, UserResource> = { ...current.resources };
  const teamIdByTemp = new Map<string, string>();
  const personIdByTemp = new Map<string, string>();

  for (const row of preview.teams) {
    if (row.action !== "create" && row.action !== "update") continue;
    if (row.action === "update" && row.id && teams[row.id]) {
      teamIdByTemp.set(row.tempKey, row.id);
      const cur = teams[row.id]!;
      teams[row.id] = {
        ...cur,
        name: row.name,
        description: row.description,
        color: row.color,
        leaderId: row.leaderRaw ? cur.leaderId : undefined,
      };
    } else if (row.action === "create") {
      const id = newId("team");
      teamIdByTemp.set(row.tempKey, id);
      teams[id] = { id, name: row.name, description: row.description, color: row.color };
    }
  }

  for (const row of preview.people) {
    if (row.action !== "create" && row.action !== "update") continue;
    const resolvedTeamId = row.teamId ?? (row.teamTempKey ? teamIdByTemp.get(row.teamTempKey) : undefined);
    if (row.action === "update" && row.id && resources[row.id]) {
      personIdByTemp.set(row.tempKey, row.id);
      const cur = resources[row.id]!;
      resources[row.id] = {
        ...cur,
        displayName: row.displayName,
        email: row.email,
        roleTitle: row.roleTitle,
        teamId: row.teamRaw ? resolvedTeamId : undefined,
        employmentType: row.employmentType ?? cur.employmentType,
        grade: row.grade ?? cur.grade,
        skills: row.skills,
        defaultMonthlyRate: row.defaultMonthlyRate,
        defaultDailyRate: row.defaultDailyRate,
        notes: row.notes,
        isActive: row.isActive,
      };
    } else if (row.action === "create" && row.employmentType && row.grade) {
      const id = newId("res");
      personIdByTemp.set(row.tempKey, id);
      resources[id] = {
        id,
        displayName: row.displayName,
        email: row.email,
        roleTitle: row.roleTitle,
        teamId: resolvedTeamId,
        employmentType: row.employmentType,
        grade: row.grade,
        skills: row.skills,
        defaultMonthlyRate: row.defaultMonthlyRate,
        defaultDailyRate: row.defaultDailyRate,
        notes: row.notes,
        isActive: row.isActive,
      };
    }
  }

  for (const row of preview.teams) {
    if (row.action !== "create" && row.action !== "update") continue;
    const id = row.id ?? teamIdByTemp.get(row.tempKey);
    if (!id || !teams[id]) continue;
    if (!row.leaderRaw) {
      teams[id] = { ...teams[id]!, leaderId: undefined };
      continue;
    }
    const leaderId = row.leaderResourceId ?? (row.leaderTempKey ? personIdByTemp.get(row.leaderTempKey) : undefined);
    teams[id] = { ...teams[id]!, leaderId };
  }

  return { teams, resources };
}
